import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'dark' | 'light' | 'system';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

interface ThemeProviderState {
  theme: Theme;
}

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined);

/**
 * The theme the app renders in, which is now only ever the one it is given.
 *
 * Nothing switches it any more: the menu's toggle was the last control, and with it went the reason to
 * remember a choice. A saved one is not read either — it would have stranded anybody who had already
 * picked dark in a theme with no way out of it.
 */
export function ThemeProvider({ children, defaultTheme = 'light' }: ThemeProviderProps): React.ReactElement {
  const [theme] = useState<Theme>(defaultTheme);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = useMemo<ThemeProviderState>(() => ({ theme }), [theme]);

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export const useTheme = (): ThemeProviderState => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
